import React, { useState, useEffect } from "react";
import {
  DiagramComponent,
  Inject,
  DataBinding,
  MindMap,
  IClickEventArgs,
  NodeModel,
  Node,
  Diagram,
  ConnectorModel,
  IKeyEventArgs,
  SnapConstraints,
  NodeConstraints,
} from "@syncfusion/ej2-react-diagrams";
import { DataManager, Query } from "@syncfusion/ej2-data";
import useAppSelector from "../../../hooks/useAppSelector";
import { RootState } from "../../../redux/store";
import { Bookmark } from "../../../types/app";
import { getMindmapState } from "../../../firebase/utils";
import Loader from "../../Loader";
import { ViewFile } from "../../../utils/fnsRedux";

type Data = {
  id: number;
  Label: string;
  type: string;
  parentId?: number;
  offsetX?: number;
  offsetY?: number;
  displayText?: string;
  group?: string;
};

type Props = {
  reset: boolean;
  setReset: React.Dispatch<React.SetStateAction<boolean>>;
};

function Mindmap(prop: Props) {
  const bookmarkState = useAppSelector((state: RootState) => state.bookmark);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [saved, setSaved] = useState<boolean>(false);
  const [updatedData, setUpdatedData] = useState<Data[]>([]);
  const [currSelected, setCurrSelected] = useState<string>("");
  const onClick = ViewFile();
  let data: Data[] = [];
  let diagramInstance: DiagramComponent;

  async function getGroups(savedData?: any) {
    const groups: Record<string, Bookmark[]> = {};
    bookmarkState.bookmarks.forEach((bookmark: Bookmark) => {
      if (groups[bookmark.group]) {
        groups[bookmark.group].push(bookmark);
      } else {
        groups[bookmark.group] = [bookmark];
      }
    });
    updateNodes(groups, savedData);
  }

  function updateNodes(groups: Record<string, Bookmark[]>, savedData?: any) {
    data.push({
      id: 1,
      Label: "Master",
      type: "Root",
      offsetX: savedData ? (savedData["nodes"][0]["offsetX"] as number) : 0,
      offsetY: savedData ? (savedData["nodes"][0]["offsetY"] as number) : 0,
    });

    let idx = 1;
    let missingGrpIdx = 0;
    let missingTitleIdx = 0;
    let missingQueryIdx = 0;
    const grpMultiplier = 50;
    const titleMultiplier = 100;
    const queryMultiplier = 200;
    Object.keys(groups).forEach((group) => {
      idx += 1;
      savedData &&
        !savedData["nodes"].find(
          (node: Node) => (node.data as Data).Label === group
        ) &&
        missingGrpIdx++;
      const currParent = idx;
      data.push({
        id: idx,
        Label: group,
        parentId: 1,
        type: "Group",
        offsetX: savedData
          ? savedData["nodes"].find(
              (node: Node) => (node.data as Data).Label === group
            )?.offsetX || 0 + missingGrpIdx * grpMultiplier
          : 0,
        offsetY: savedData
          ? savedData["nodes"].find(
              (node: Node) => (node.data as Data).Label === group
            )?.offsetY || 0
          : 0,
      });
      const titles: Record<string, number> = {};
      groups[group].forEach((bookmark) => {
        if (!(bookmark.title in titles)) {
          idx += 1;
          titles[bookmark.title] = idx;
          savedData &&
            !savedData["nodes"].find(
              (node: Node) =>
                (node.data as Data).group === group &&
                (node.data as Data).Label === bookmark.title
            ) &&
            missingTitleIdx++;
          data.push({
            id: idx,
            Label: bookmark.title,
            parentId: currParent,
            type: "Title",
            group: group,
            offsetX: savedData
              ? savedData["nodes"].find(
                  (node: Node) =>
                    (node.data as Data).group === group &&
                    (node.data as Data).Label === bookmark.title
                )?.offsetX || 0 + missingTitleIdx * titleMultiplier
              : 0,
            offsetY: savedData
              ? savedData["nodes"].find(
                  (node: Node) =>
                    (node.data as Data).group === group &&
                    (node.data as Data).Label === bookmark.title
                )?.offsetY || 30
              : 0,
          });
        }
      });
      Object.keys(titles).forEach((title) => {
        const bookmarkWithTitle = groups[group].filter(
          (bookmark) => bookmark.title === title
        );
        bookmarkWithTitle.forEach((bookmark) => {
          idx += 1;
          savedData &&
            !savedData["nodes"].find(
              (node: Node) =>
                (node.data as Data).displayText === bookmark.displayText &&
                (node.data as Data).group === bookmark.group
            ) &&
            missingQueryIdx++;
          data.push({
            id: idx,
            Label: bookmark.answer || bookmark.description,
            displayText: bookmark.displayText,
            group: group,
            parentId: titles[title],
            type: "Description",
            offsetX: savedData
              ? savedData["nodes"].find(
                  (node: Node) =>
                    (node.data as Data).displayText === bookmark.displayText &&
                    (node.data as Data).group === bookmark.group
                )?.offsetX || 0 + missingQueryIdx * queryMultiplier
              : 0,
            offsetY: savedData
              ? savedData["nodes"].find(
                  (node: Node) =>
                    (node.data as Data).displayText === bookmark.displayText &&
                    (node.data as Data).group === bookmark.group
                )?.offsetY || 60
              : 0,
          });
        });
      });
    });
    setUpdatedData(data);
    setLoaded(true);
  }

  useEffect(() => {
    getMindmapState().then((res) => {
      if (bookmarkState.bookmarksLoaded) {
        const saved = res !== null;
        setSaved(saved);
        if (saved) {
          getGroups(JSON.parse(res!.state));
        } else {
          getGroups();
        }
      }
    });
  }, [bookmarkState.bookmarks.length]);

  return loaded ? (
    <DiagramComponent
      id="diagram"
      width="100%"
      height="100%"
      backgroundColor="white"
      ref={(diagram) => (diagramInstance = diagram!)}
      layout={{
        type: !saved || prop.reset ? "MindMap" : "None",
        orientation: "Horizontal",
      }}
      created={() => {
        diagramInstance.fitToPage({
          // canZoomIn: true,
        });
      }}
      //Configures data source for diagram
      dataSourceSettings={{
        id: "id",
        parentId: "parentId",
        dataManager: new DataManager(updatedData, new Query().take(1)),
        root: String(1),
      }}
      snapSettings={{ constraints: SnapConstraints.None }}
      //Sets the default properties for nodes and connectors
      getNodeDefaults={(obj: NodeModel) => {
        obj.shape = {
          type: "Basic",
          shape:
            (obj.data as Data).type === "Root" ||
            (obj.data as Data).type === "Group"
              ? "Ellipse"
              : "Rectangle",
        };
        obj.style = {
          fill:
            (obj.data as Data).type === "Root"
              ? "#c2e2f0"
              : (obj.data as Data).type === "Group"
              ? "#dbe5c4"
              : (obj.data as Data).type === "Title"
              ? "#e6d1d1"
              : "#d4c3e4",
          strokeColor:
            (obj.data as Data).type === "Root"
              ? "#1a8cbd"
              : (obj.data as Data).type === "Group"
              ? "#95bd3a"
              : (obj.data as Data).type === "Title"
              ? "#cf8080"
              : "#a47acc",
          strokeWidth: 2,
        };
        obj.annotations = [
          {
            content: (obj.data as Data).Label,
            margin: { left: 10, right: 10, top: 10, bottom: 10 },
          },
        ];
        obj.constraints = NodeConstraints.Default | NodeConstraints.ReadOnly;
        obj.borderWidth = 1;
        obj.maxHeight = 30;
        obj.offsetX = (obj.data as Data).offsetX;
        obj.offsetY = (obj.data as Data).offsetY;
        return obj;
      }}
      getConnectorDefaults={(connector: ConnectorModel, diagram: Diagram) => {
        connector.style = {
          strokeColor: "#6BA5D7",
          strokeWidth: 3,
        };
        connector.targetDecorator!.style!.fill = "#6BA5D7";
        connector.targetDecorator!.style!.strokeColor = "#6BA5D7";
        connector.type = "Bezier";
        return connector;
      }}
      click={(args: IClickEventArgs) => {
        if (
          args.element instanceof Node &&
          (args.element as any).properties.data.type === "Description"
        ) {
          setCurrSelected((args.element as any).properties.data.displayText);
        } else {
          setCurrSelected("");
        }
        prop.setReset(false);
        console.log(args);
      }}
      keyDown={(args: IKeyEventArgs) => {
        if (args.key === "Enter" && currSelected !== "") {
          const selectedBookmark = bookmarkState.bookmarks.find(
            (bookmark) => bookmark.displayText === currSelected
          );
          onClick(
            selectedBookmark!.searchText,
            selectedBookmark!.pdfName,
            selectedBookmark!.page
          );
        }
      }}
    >
      <Inject services={[DataBinding, MindMap]} />
    </DiagramComponent>
  ) : (
    <Loader />
  );
}

export default Mindmap;
